import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactNode } from "react";
import styled from "styled-components";

interface PageTitleBarProps {
  title: string;
  children?: ReactNode;
  showDivider?: boolean;
  variant?: "small" | "big";
  onBack?: () => void;
}

/**
 * Reusable page title bar component for consistent title styling across routes.
 * Works for both secondary drawer routes and full screen routes.
 *
 * @param title - The title text to display
 * @param children - Optional content to display after the title (e.g., action buttons)
 * @param showDivider - Whether to show a divider below the title bar (default: false for secondary drawer routes, true for full screen routes)
 */
export default function PageTitleBar({
  title,
  children,
  showDivider = false,
  variant = "small",
  onBack,
}: PageTitleBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const titleFontSize = variant === "big" ? "1.5rem" : "1rem";
  const titleFontWeight = variant === "big" ? 700 : 600;

  return (
    <>
      {/* Desktop header */}
      {!isMobile && (
        <Header>
          {onBack && (
            <BackButton onClick={onBack} edge="start" size="large">
              <ArrowBackIcon />
            </BackButton>
          )}
          <TitleContainer hasBackButton={!!onBack}>
            <Typography
              variant={variant === "big" ? "h4" : "h6"}
              component="h1"
              sx={{
                fontSize: titleFontSize,
                fontWeight: titleFontWeight,
              }}
            >
              {title}
            </Typography>
            {children && <ChildrenContainer>{children}</ChildrenContainer>}
          </TitleContainer>
        </Header>
      )}

      {/* Mobile header */}
      {isMobile && (
        <Box
          sx={{
            minHeight: "calc(56px + env(safe-area-inset-top))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "calc(env(safe-area-inset-top) + 12px)",
            paddingBottom: "12px",
            px: 3,
            position: "relative",
            mb: showDivider ? 0 : 3,
          }}
        >
          {onBack && (
            <IconButton
              onClick={onBack}
              sx={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                marginTop: "calc(env(safe-area-inset-top) / 2)",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.1)" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant={variant === "big" ? "h4" : "h6"}
            component="h1"
            sx={{
              mb: 0,
              fontSize: titleFontSize,
              fontWeight: titleFontWeight,
              textAlign: "center",
              flex: 1,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {children && (
            <Box
              sx={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                marginTop: "calc(env(safe-area-inset-top) / 2)",
              }}
            >
              {children}
            </Box>
          )}
        </Box>
      )}

      {showDivider && (
        <Divider
          sx={{
            borderColor: "rgba(222, 222, 222, 1)",
            "&::before, &::after": {
              borderColor: "rgba(222, 222, v, 1) !important",
            },
          }}
        />
      )}
    </>
  );
}

const Header = styled.div`
  background-color: white;
  padding: 16px;
  padding-top: calc(env(safe-area-inset-top) + 16px);
  display: flex;
  align-items: center;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
  position: relative;
`;

const BackButton = styled(IconButton)`
  position: absolute;
  left: 8px;
`;

const TitleContainer = styled.div<{ hasBackButton: boolean }>`
  flex: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 0 ${(props) => (props.hasBackButton ? "48px" : "0")};
`;

const ChildrenContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
